import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { MediaStage } from "@/core/types/mediaTypes";
import { ReviewStage } from "@/domains/LibraryView/reviewStage";
import { useVideoPreloader } from "./useVideoPreloader";
import { useAppStore } from "@/core/store/useAppStore";
import { imageActionsService } from "@/core/MediaItem/ImageActionsService";
import { ChevronLeftIcon, ChevronRightIcon, VideoIcon, ErrorIcon, SpinnerIcon, CheckIcon } from "@/components/icons";
import {
  useReviewKeyboard,
  useBodyScrollLock,
  useReviewQueue,
  LeaderModeIndicator,
  DownloadStatus,
  ReviewSidebar,
  ReviewHeader,
} from "../ReviewModal";

interface VideoReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  stage?: ReviewStage;
  /** Which video content type to review. Defaults to general "videos". */
  contentType?: "videos" | "reels";
  editorMode?: boolean;
  editorToken?: string;
  videos?: any[];
  getStreamUrlByFileId?: (fileId: string) => string | undefined;
  initialVideoId?: number;
  /** Reports the current item id as the user navigates, for URL persistence. */
  onCurrentItemChange?: (mediaId: number) => void;
}

export const VideoReviewModal: React.FC<VideoReviewModalProps> = ({
  isOpen,
  onClose,
  clientId,
  stage,
  contentType = "videos",
  editorMode = false,
  editorToken,
  initialVideoId,
  onCurrentItemChange,
}) => {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>(null);
  const [addToCollectionStatus, setAddToCollectionStatus] = useState<"adding" | "added" | "no-collection" | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessStatus, setReprocessStatus] = useState<"success" | "error" | null>(null);

  const {
    collections: collectionsState,
    fetchCollections,
    setActiveCollection,
    addItemsToActiveCollection,
    isInActiveCollection,
  } = useAppStore();

  const { collections, activeCollectionId } = collectionsState;

  useEffect(() => {
    if (isOpen && clientId && collectionsState.lastFetchedClientId !== clientId) {
      fetchCollections(clientId);
    }
  }, [isOpen, clientId, collectionsState.lastFetchedClientId, fetchCollections]);

  const {
    items: videos,
    currentItem: currentVideo,
    currentIndex,
    total,
    isLoading,
    error,
    navigateNext,
    navigatePrev,
    canNavigateNext,
    canNavigatePrev,
    updateItemStage: updateVideoStage,
    updateItemTags,
    downloadCurrentItem: downloadCurrentVideo,
  } = useReviewQueue({
    clientId,
    stage,
    contentType,
    isOpen,
    editorMode,
    editorToken,
    initialMediaId: initialVideoId,
  });

  // Report the current item id upward (URL persistence).
  useEffect(() => {
    if (isOpen && currentVideo) {
      onCurrentItemChange?.(currentVideo.id);
    }
  }, [isOpen, currentVideo, onCurrentItemChange]);

  useEffect(() => {
    setIsVideoLoading(true);
    setVideoError(null);
    setReprocessStatus(null);
    setIsReprocessing(false);
  }, [currentVideo?.id]);

  const goToNext = useCallback(() => {
    if (canNavigateNext) {
      navigateNext();
    }
  }, [canNavigateNext, navigateNext]);

  const retryVideo = useCallback(() => {
    setVideoError(null);
    setIsVideoLoading(true);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.load();
    }
  }, []);

  const reprocessVideo = useCallback(async () => {
    if (!currentVideo || isReprocessing) return;

    setIsReprocessing(true);
    setReprocessStatus(null);

    try {
      await imageActionsService.reprocessVideo(clientId, currentVideo.id);
      setReprocessStatus("success");
      setVideoError(null);
    } catch (err) {
      console.error("Failed to reprocess video:", err);
      setReprocessStatus("error");
    } finally {
      setIsReprocessing(false);
    }
  }, [currentVideo, clientId, isReprocessing]);

  const { preloadRef1, preloadRef2 } = useVideoPreloader({
    videos,
    currentIndex,
    enabled: isOpen && !isLoading,
  });

  const videoPlayerRef = useRef<HTMLVideoElement | null>(null);

  useBodyScrollLock(isOpen);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const skipForward = useCallback(() => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime += 3;
    }
  }, []);

  const skipBackward = useCallback(() => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime -= 3;
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    if (videoPlayerRef.current) {
      if (videoPlayerRef.current.paused) {
        videoPlayerRef.current.play();
      } else {
        videoPlayerRef.current.pause();
      }
    }
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      await downloadCurrentVideo((status) => {
        setDownloadStatus(status);
        if (status === "ready" || status === "failed") {
          setTimeout(() => setDownloadStatus(null), 3000);
        }
      });
    } catch (err) {
      console.error("Download failed:", err);
      setDownloadStatus("failed");
      setTimeout(() => setDownloadStatus(null), 3000);
    }
  }, [downloadCurrentVideo]);

  const handleStageChange = useCallback(
    async (newStage: MediaStage) => {
      try {
        await updateVideoStage(newStage);
      } catch (err) {
        console.error("Stage change failed:", err);
      }
    },
    [updateVideoStage]
  );

  const handleAddToCollection = useCallback(async () => {
    if (!currentVideo) return;

    if (!activeCollectionId) {
      setAddToCollectionStatus("no-collection");
      setTimeout(() => setAddToCollectionStatus(null), 2000);
      return;
    }

    if (isInActiveCollection(currentVideo.id)) {
      setAddToCollectionStatus("added");
      setTimeout(() => setAddToCollectionStatus(null), 1500);
      return;
    }

    setAddToCollectionStatus("adding");
    try {
      await addItemsToActiveCollection(clientId, [currentVideo.id]);
      setAddToCollectionStatus("added");
      setTimeout(() => setAddToCollectionStatus(null), 1500);
    } catch (err) {
      console.error("Failed to add to collection:", err);
      setAddToCollectionStatus(null);
    }
  }, [currentVideo, activeCollectionId, clientId, addItemsToActiveCollection, isInActiveCollection]);

  const keyboardHandlers = useMemo(
    () => ({
      onNavigateNext: canNavigateNext ? navigateNext : undefined,
      onNavigatePrev: canNavigatePrev ? navigatePrev : undefined,
      onStageChange: !editorMode ? handleStageChange : undefined,
      onDownload: handleDownload,
      onClose: handleClose,
      onPlayPause: togglePlayPause,
      onSkipForward: skipForward,
      onSkipBackward: skipBackward,
      onAddToCollection: !editorMode ? handleAddToCollection : undefined,
    }),
    [
      canNavigateNext,
      canNavigatePrev,
      navigateNext,
      navigatePrev,
      editorMode,
      handleStageChange,
      handleDownload,
      handleClose,
      togglePlayPause,
      skipForward,
      skipBackward,
      handleAddToCollection,
    ]
  );

  const { leaderModeActive } = useReviewKeyboard({
    isOpen,
    handlers: keyboardHandlers,
    canNavigateNext,
    canNavigatePrev,
    editorMode,
  });

  const isCurrentInCollection = currentVideo ? isInActiveCollection(currentVideo.id) : false;

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <LeaderModeIndicator isActive={leaderModeActive} />

      <video ref={preloadRef1} style={{ display: "none" }} preload="auto" />
      <video ref={preloadRef2} style={{ display: "none" }} preload="auto" />

      <ReviewHeader
        onClose={handleClose}
        title="Video"
        currentIndex={currentIndex}
        total={total}
        stage={currentVideo?.stage}
        inPostCount={currentVideo?.usageCount ?? 0}
      />

      <div className="flex-1 flex min-h-0">
        <div className="flex-1 flex items-center justify-center relative bg-black">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <SpinnerIcon className="w-12 h-12 text-cyan-500" />
              <p className="text-gray-400">Loading videos...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-4 text-red-400">
              <ErrorIcon className="w-12 h-12" />
              <p>{error}</p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-sm text-white transition-colors"
              >
                Close
              </button>
            </div>
          ) : videos.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 text-gray-400">
              <VideoIcon className="w-16 h-16" />
              <p>No videos to review</p>
            </div>
          ) : currentVideo ? (
            <>
              <video
                ref={videoPlayerRef}
                key={currentVideo.id}
                src={currentVideo.getStreamUrlWithQueryAuth(true)}
                className="max-w-full max-h-full object-contain"
                controls
                autoPlay
                onLoadStart={() => setIsVideoLoading(true)}
                onCanPlay={() => setIsVideoLoading(false)}
                onError={() => {
                  setIsVideoLoading(false);
                  setVideoError("Failed to load video");
                }}
              />

              {isVideoLoading && !videoError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 pointer-events-none">
                  <SpinnerIcon className="w-12 h-12 text-white" />
                </div>
              )}

              {videoError && (
                <VideoErrorOverlay
                  reprocessStatus={reprocessStatus}
                  isReprocessing={isReprocessing}
                  onReprocess={reprocessVideo}
                  onRetry={retryVideo}
                  onSkip={canNavigateNext ? goToNext : undefined}
                />
              )}

              <button
                onClick={canNavigatePrev ? navigatePrev : undefined}
                disabled={!canNavigatePrev}
                className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white transition-colors ${
                  canNavigatePrev
                    ? "bg-black/60 hover:bg-black/80 cursor-pointer"
                    : "bg-black/30 opacity-30 cursor-not-allowed"
                }`}
              >
                <ChevronLeftIcon className="w-8 h-8" />
              </button>

              <button
                onClick={canNavigateNext ? navigateNext : undefined}
                disabled={!canNavigateNext}
                className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white transition-colors ${
                  canNavigateNext
                    ? "bg-black/60 hover:bg-black/80 cursor-pointer"
                    : "bg-black/30 opacity-30 cursor-not-allowed"
                }`}
              >
                <ChevronRightIcon className="w-8 h-8" />
              </button>
            </>
          ) : null}
        </div>

        <ReviewSidebar
          editorMode={editorMode}
          onStageChange={handleStageChange}
          currentStage={currentVideo?.stage as MediaStage | undefined}
          collections={collections}
          activeCollectionId={activeCollectionId}
          onCollectionChange={setActiveCollection}
          onAddToCollection={handleAddToCollection}
          addToCollectionStatus={addToCollectionStatus}
          isCurrentInCollection={isCurrentInCollection}
          downloadStatus={downloadStatus}
          onDownload={handleDownload}
          showPlaybackControls
          onPlayPause={togglePlayPause}
          onSkipBackward={skipBackward}
          onSkipForward={skipForward}
          clientId={clientId}
          mediaItem={currentVideo ?? null}
          onTagsPersisted={updateItemTags}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const VideoErrorOverlay: React.FC<{
  reprocessStatus: "success" | "error" | null;
  isReprocessing: boolean;
  onReprocess: () => void;
  onRetry: () => void;
  onSkip?: () => void;
}> = ({ reprocessStatus, isReprocessing, onReprocess, onRetry, onSkip }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white">
    <ErrorIcon className="w-12 h-12 mb-4 text-red-400" />
    <p className="mb-2 text-lg font-medium">Video proxy not available</p>
    <p className="mb-6 text-sm text-gray-400 max-w-md text-center">
      The video file may not have been processed yet, or the proxy was deleted.
      You can trigger reprocessing to regenerate it.
    </p>

    {reprocessStatus === "success" && (
      <div className="mb-4 px-4 py-2 bg-green-900/50 border border-green-700 rounded-lg text-green-300 text-sm">
        Video queued for reprocessing. This may take a few minutes.
      </div>
    )}
    {reprocessStatus === "error" && (
      <div className="mb-4 px-4 py-2 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
        Failed to queue reprocessing. Please try again.
      </div>
    )}

    <div className="flex gap-3">
      <button
        onClick={onReprocess}
        disabled={isReprocessing || reprocessStatus === "success"}
        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-900 disabled:cursor-not-allowed rounded-lg text-white transition-colors flex items-center gap-2"
      >
        {isReprocessing ? (
          <>
            <SpinnerIcon className="w-4 h-4" />
            Reprocessing...
          </>
        ) : reprocessStatus === "success" ? (
          <>
            <CheckIcon className="w-4 h-4" />
            Queued
          </>
        ) : (
          "Reprocess Video"
        )}
      </button>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
      >
        Retry
      </button>
      {onSkip && (
        <button
          onClick={onSkip}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-white transition-colors"
        >
          Skip
        </button>
      )}
    </div>
  </div>
);
